/**
 * Pure link-skill derivation logic (WP7 spec: "Projection/derivation logic lives in pure,
 * unit-tested lib functions" — `lib/links.ts` is named explicitly).
 *
 * The bracket rank/roster-floor rules mirror `backend/.../links/LinkBrackets.java` exactly (the two
 * languages can't share a literal source, so keep them in sync by hand — see that file's own
 * comment). `LinkProgressService` already computes `effectiveBracket` server-side per class; this
 * module's job is the client-only aggregation on top of it: shared-group stacking and the totals
 * overview.
 */
import type { ClassDto } from './reference';

export type Bracket = 'NONE' | 'L70' | 'L120' | 'L210';

export const BRACKET_ORDER: readonly Bracket[] = ['NONE', 'L70', 'L120', 'L210'];

export interface LinkProgressDto {
  classSlug: string;
  storedBracket: string;
  derivedBracket: string;
  effectiveBracket: string;
  derivedFromCharacterName: string | null;
  derivedFromLevel: number | null;
}

export function rankOfBracket(bracket: string): number {
  const index = BRACKET_ORDER.indexOf(bracket as Bracket);
  return index < 0 ? 0 : index;
}

/** The bracket a rostered character's level implies as a floor — same thresholds as the backend. */
export function bracketFromLevel(level: number): Bracket {
  if (level >= 210) return 'L210';
  if (level >= 120) return 'L120';
  if (level >= 70) return 'L70';
  return 'NONE';
}

export interface GroupTotal {
  sharedGroup: string;
  classSlugs: string[];
  totalLevels: number;
  maxLevels: number;
}

/**
 * Sums each shared group's members' effective link levels (0-3 each, from `rankOfBracket`) against
 * the group's `max_level` (identical across every member of a group in the seed data — e.g. the
 * 3-class Explorer Warriors group's `max_level` is 9, exactly 3x3, so no separate "group cap" input
 * is needed beyond one member's own `link_skill.maxLevel`).
 */
export function shareGroupTotals(
  classes: readonly ClassDto[],
  progress: readonly LinkProgressDto[],
): GroupTotal[] {
  const effectiveByClass = new Map(progress.map((p) => [p.classSlug, rankOfBracket(p.effectiveBracket)]));
  const groups = new Map<string, GroupTotal>();
  for (const klass of classes) {
    const sharedGroup = klass.linkSkill?.sharedGroup;
    if (!sharedGroup) {
      continue;
    }
    const existing = groups.get(sharedGroup);
    const level = effectiveByClass.get(klass.slug) ?? 0;
    if (existing) {
      existing.classSlugs.push(klass.slug);
      existing.totalLevels += level;
    } else {
      groups.set(sharedGroup, {
        sharedGroup,
        classSlugs: [klass.slug],
        totalLevels: level,
        maxLevels: klass.linkSkill?.maxLevel ?? 0,
      });
    }
  }
  return [...groups.values()];
}

/**
 * Human-readable names for the seeded `shared_group` slugs (see
 * `backend/src/main/resources/seed/classes.json`'s `linkSkills[].sharedGroup`) — those are
 * normalized identifiers for backend grouping, never meant for display. Known groups get a
 * curated name; anything unmapped (a future seed addition) falls back to a generic slug
 * humanizer so raw slugs never leak into the UI.
 */
const SHARED_GROUP_LABELS: Record<string, string> = {
  explorer_warriors: 'Explorer Warriors',
  explorer_magicians: 'Explorer Magicians',
  explorer_bowmen: 'Explorer Bowmen',
  explorer_thieves: 'Explorer Thieves',
  explorer_pirates: 'Explorer Pirates',
  cygnus_knights: 'Cygnus Knights',
  resistance_link: 'Resistance',
  shine_guiding_stars: 'Shine',
};

/** Splits on `-`/`_` and title-cases each word — e.g. `zero_link` -> `Zero Link`. */
function humanizeSlug(slug: string): string {
  return slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function formatSharedGroupName(sharedGroup: string): string {
  return SHARED_GROUP_LABELS[sharedGroup] ?? humanizeSlug(sharedGroup);
}

export interface LinkTotals {
  /** Classes whose effective bracket is L210 (rank 3, the max this app tracks). */
  maxedCount: number;
  totalLevelsReached: number;
  byBranch: Map<string, number>;
}

/** The `/links` page's "totals overview": links at max / total levels reached / count by branch. */
export function accountLinkTotals(classes: readonly ClassDto[], progress: readonly LinkProgressDto[]): LinkTotals {
  const effectiveByClass = new Map(progress.map((p) => [p.classSlug, rankOfBracket(p.effectiveBracket)]));
  let maxedCount = 0;
  let totalLevelsReached = 0;
  const byBranch = new Map<string, number>();
  for (const klass of classes) {
    const level = effectiveByClass.get(klass.slug) ?? 0;
    totalLevelsReached += level;
    if (level >= 3) {
      maxedCount++;
    }
    byBranch.set(klass.branch, (byBranch.get(klass.branch) ?? 0) + level);
  }
  return { maxedCount, totalLevelsReached, byBranch };
}
